import React, { useMemo, useState } from 'react';
import type { MultipleChoiceQuestion as MultipleChoiceQuestionType } from './survey.types';
import { useLanguageStore } from '../../stores/languageStore';
import LanguageHelper from '../../utils/languageHelper';

interface MultipleChoiceQuestionProps {
  question: MultipleChoiceQuestionType;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  displayNumber?: number;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  question,
  value,
  onChange,
  error,
  displayNumber,
}) => {
  const languageCode = useLanguageStore((state) => state.code);
  const [otherText, setOtherText] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(() => {
    const existingOther = value.find((v) => v.startsWith('other:'));
    return Boolean(existingOther);
  });

  React.useEffect(() => {
    const existingOther = value.find((v) => v.startsWith('other:'));
    if (existingOther) {
      const text = existingOther.substring('other:'.length);
      setShowOtherInput(true);
      // Only update otherText if it's different to avoid overwriting user input
      setOtherText(prevText => prevText === text ? prevText : text);
      return;
    }
    // If there's no stored "Other" value, keep the input visible if the user has toggled it on,
    // but clear any stale text when it's not shown.
    if (!showOtherInput) {
      setOtherText('');
    }
  }, [value, showOtherInput]);

  const displayChoices = useMemo(() => {
    if (question.shuffleOptions) {
      return shuffleArray(question.choices);
    }
    return question.choices;
  }, [question.choices, question.shuffleOptions]);

  const maxSelectable = question.maxSelectable;
  const minSelectable = question.minSelectable;

  const handleCheckboxChange = (choiceValue: string) => {
    const isCurrentlySelected = value.includes(choiceValue);

    if (isCurrentlySelected) {
      // Deselecting - always allow (unless max=1, then keep it selected)
      if (maxSelectable === 1) {
        // For max=1 (radio behavior), don't allow deselecting
        return;
      }
      const newValue = value.filter((v) => v !== choiceValue);
      onChange(newValue);
      return;
    }

    // Selecting a new choice
    if (maxSelectable === 1) {
      // For max=1, replace all selections with this one (radio button behavior)
      setShowOtherInput(false);
      setOtherText('');
      onChange([choiceValue]);
      return;
    }

    // For max > 1, count current selections
    const regularChoicesCount = value.filter((v) => !v.startsWith('other:')).length;
    const otherCount = showOtherInput ? 1 : 0;
    const totalSelections = regularChoicesCount + otherCount;

    if (maxSelectable && totalSelections >= maxSelectable) {
      // Already at max selections, can't add more
      return;
    }

    onChange([...value, choiceValue]);
  };

  const handleOtherCheckboxChange = () => {
    const isOtherSelected = showOtherInput;

    if (isOtherSelected) {
      // Unchecking "Other"
      if (maxSelectable === 1) {
        // For max=1 (radio behavior), don't allow deselecting
        return;
      }
      // For max > 1, allow unchecking - remove any "other:" values
      setShowOtherInput(false);
      onChange(value.filter((v) => !v.startsWith('other:')));
      setOtherText('');
      return;
    }

    // Checking "Other"
    if (maxSelectable === 1) {
      // For max=1, replace all selections with Other (radio button behavior)
      setShowOtherInput(true);
      // Store text as-is to allow spaces during input. Trimming happens during validation.
      onChange([`other:${otherText}`]);
      return;
    }

    // For max > 1, count current selections (not including Other since we're adding it)
    const regularChoices = value.filter((v) => !v.startsWith('other:'));

    if (maxSelectable && regularChoices.length >= maxSelectable) {
      // Already at max selections, can't add more
      return;
    }

    // Have room to add "Other"
    setShowOtherInput(true);
    // Store text as-is to allow spaces during input. Trimming happens during validation.
    onChange([...regularChoices, `other:${otherText}`]);
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    const filteredValues = value.filter((v) => !v.startsWith('other:'));
    // Store text as-is to allow spaces during input. Trimming happens during validation.
    onChange([...filteredValues, `other:${text}`]);
  };

  const getSelectionHelperText = () => {
    if (minSelectable && maxSelectable) {
      if (minSelectable === maxSelectable) {
        return `${LanguageHelper.getExtendedTranslation(languageCode, 'selectExactly')} ${minSelectable} ${LanguageHelper.getOptionText(languageCode, minSelectable)}`;
      }
      return `${LanguageHelper.getExtendedTranslation(languageCode, 'selectBetween')} ${minSelectable} ${LanguageHelper.getExtendedTranslation(languageCode, 'to')} ${maxSelectable} ${LanguageHelper.getOptionText(languageCode, maxSelectable)}`;
    }
    if (maxSelectable) {
      return `${LanguageHelper.getExtendedTranslation(languageCode, 'selectUpTo')} ${maxSelectable} ${LanguageHelper.getOptionText(languageCode, maxSelectable)}`;
    }
    if (minSelectable) {
      return `${LanguageHelper.getExtendedTranslation(languageCode, 'selectAtLeast')} ${minSelectable} ${LanguageHelper.getOptionText(languageCode, minSelectable)}`;
    }
    return null;
  };

  const selectionHelperText = getSelectionHelperText();

  // Count total selections: regular choices + "Other" if checkbox is checked
  const regularChoicesCount = value.filter((v) => !v.startsWith('other:')).length;
  const otherCount = showOtherInput ? 1 : 0;
  const totalSelections = regularChoicesCount + otherCount;

  // For max=1, don't disable other options (allow switching like radio buttons)
  // For max>1, disable unselected options when at max
  const isAtMaxSelections = maxSelectable && maxSelectable > 1 ? totalSelections >= maxSelectable : false;
  const specifyText = LanguageHelper.getExtendedTranslation(languageCode, 'pleaseSpecify');

  return (
    <div className="survey-question multiple-choice-question">
      <label className="question-label">
        {displayNumber != null && (
          <span className="question-number">{displayNumber}.</span>
        )}
        <span dangerouslySetInnerHTML={{ __html: question.question_text[languageCode] }} />
        {question.is_required && <span className="required-indicator">*</span>}
      </label>
      {selectionHelperText && (
        <div className="question-helper-text">{selectionHelperText}</div>
      )}
      <div className="choices-container">
        {displayChoices.map((choice) => {
          const choiceValue = `choice_${choice.choice_number}`;
          const isSelected = value.includes(choiceValue);
          const isDisabled = !isSelected && isAtMaxSelections;
          return (
            <label key={choice.choice_number} className={`choice-label ${isDisabled ? 'choice-disabled' : ''}`}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleCheckboxChange(choiceValue)}
                className="choice-checkbox"
                disabled={isDisabled}
              />
              <span className="choice-text">{choice.choice_text[languageCode]}</span>
            </label>
          );
        })}
        {question.showOther && (
          <div className="other-choice">
            <label className={`choice-label ${!showOtherInput && isAtMaxSelections ? 'choice-disabled' : ''}`}>
              <input
                type="checkbox"
                checked={showOtherInput}
                onChange={handleOtherCheckboxChange}
                className="choice-checkbox"
                disabled={!showOtherInput && isAtMaxSelections}
              />
              <span className="choice-text">
                {question.otherValue?.[languageCode] || LanguageHelper.getTranslations(languageCode).other}
              </span>
            </label>
            {showOtherInput && (
              <div className="other-input-block">
                <input
                  type="text"
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  placeholder={specifyText}
                  className={`other-input ${error ? 'has-error' : ''}`}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};
